// ============================================================
// WHAT A TRAIT DRAWS ROUND THE FIGURE (plan design-gaps, T1; the traits page,
// ruled "approved as built" 2026-10-07 - these are its remaining pieces):
//   a figure above what it stands on - a flyer carried up, or anyone - a
//     dashed plumb line down to a small ring on the ground under it;
//   a hoverer's four drop lines from its lifted ring's edge to the floor;
//   a burrower's furrow, the route behind it fading over 2.5 s;
//   and the TAG under the name: what it is doing (FLYING and its height,
//     HOVERING, BURROWED, INCORPOREAL and whether it is inside the wall),
//     HALF SEEN behind translucent terrain (T3), HIDDEN on the GM's figure the
//     players do not see (Z6), and a size not a man's - its height and how
//     high it steps without climbing.
// A lying figure is down: it draws none of it.
//
// Ground points, for the renderer to project. Pure. Zero React dependency.
// ============================================================

import type { Vec3 } from '../battlefield/types';
import type { Token } from '../battlefield/tokens';
import { tokenHeight } from '../battlefield/tokens';
import { headingPoint } from '../battlefield/facing';
import { STEP_FRACTION } from '../battlefield/traitRules';
import { SIZE_HEIGHTS } from '../constants';
import type { FigureModel } from './figureModel';
import { HOVER } from './figures';
import { walkAt, type Walk } from './walk';

export interface TraitMarks {
  /** Above what it stands on: a plumb line from its ring to a small ring on the ground under it. */
  plumb: { from: Vec3; to: Vec3 } | null;
  /** A hoverer's drop lines, each from its lifted ring's edge to the floor. */
  drops: [Vec3, Vec3][];
  /** The tag under the name, or null with nothing to say. */
  tag: string | null;
}

type Placed = Pick<Token, 'x' | 'z' | 'elevation' | 'eyeHeight' | 'footprint'> & { height?: number };

/** Higher than this above its ground, a figure is up off it (a rounding error is not a lift). */
export const UP_M = 0.05;
/** A size differing from a man's by more than this is worth a tag. */
const SIZE_M = 0.05;
const MAN = SIZE_HEIGHTS.Average;
/** How long a burrower's furrow lasts behind it, and how many points it is drawn with. */
const FURROW_MS = 2500;
const FURROW_POINTS = 11;

const TRAIT_WORD: Record<NonNullable<FigureModel['trait']>, string> = {
  flyer: 'FLYING',
  hoverer: 'HOVERING',
  burrower: 'BURROWED',
  incorporeal: 'INCORPOREAL',
};

/**
 * What the marks read of the map and the table: `under` is the height of whatever the figure stands on there (the
 * top of a crate, the floor), `inside` whether an incorporeal figure is inside a wall, `halfSeen` whether it stands
 * behind translucent terrain alone (T3), `hidden` whether it is hidden from the players (Z6: only the GM's map holds
 * one), `alert` a roused watcher's word (Z2).
 */
export interface TagFlags {
  under: number;
  inside?: boolean;
  halfSeen?: boolean;
  hidden?: boolean;
  alert?: string;
}

/**
 * The marks a figure's trait and size draw. A figure down draws none of them, but keeps its STATUS words - HIDDEN,
 * a watcher's word - which are not its trait's (the Track Z simplify).
 */
export function traitMarks(token: Placed, model: FigureModel, flags: TagFlags): TraitMarks {
  if (model.posture !== 'stand') return { plumb: null, drops: [], tag: joined(statusWords(flags)) };
  const up = token.elevation - flags.under;
  const plumb = up > UP_M ? { from: at(token, token.elevation), to: at(token, flags.under) } : null;
  const drops: [Vec3, Vec3][] =
    model.trait === 'hoverer'
      ? [45, 135, 225, 315].map((deg) => {
          const edge = headingPoint(token, deg, token.footprint);
          return [at(edge, token.elevation + HOVER), at(edge, token.elevation)];
        })
      : [];
  return { plumb, drops, tag: tagOf(token, model, up, flags) };
}

const at = (p: { x: number; z: number }, y: number): Vec3 => ({ x: p.x, y, z: p.z });
const joined = (words: (string | null)[]) => {
  const said = words.filter((w): w is string => !!w);
  return said.length ? said.join(' · ') : null;
};

/**
 * The words about the figure's standing at the table, first: HIDDEN on the GM's figure the players do not see (Z6:
 * what the GM must not forget), then a roused watcher's (Z2: what the party most needs to read).
 */
const statusWords = ({ hidden, alert }: TagFlags) => [hidden ? 'HIDDEN' : null, alert ?? null];

/** The tag's words, joined: its status, what it does, how high it is, and a size not a man's. */
function tagOf(token: Placed, model: FigureModel, up: number, flags: TagFlags): string | null {
  const height = tokenHeight(token);
  return joined([
    ...statusWords(flags),
    doing(model, !!flags.inside),
    // Behind translucent terrain (T3): seen, but only half.
    flags.halfSeen ? 'HALF SEEN' : null,
    up > UP_M ? `${up.toFixed(1)} M${model.trait === 'flyer' ? '' : ' UP'}` : null,
    Math.abs(height - MAN) > SIZE_M ? `${height.toFixed(1)} M · STEPS ${(height * STEP_FRACTION).toFixed(1)} M` : null,
  ]);
}

/** What the figure is doing, by its trait. */
function doing(model: FigureModel, inside: boolean): string | null {
  if (!model.trait) return null;
  return model.trait === 'incorporeal' && inside
    ? `${TRAIT_WORD.incorporeal} · INSIDE THE WALL`
    : TRAIT_WORD[model.trait];
}

/**
 * A burrower's furrow: the route behind it over the last FURROW_MS, newest first, each point with its age (0 just
 * now, 1 about to close). Never back before the walk began.
 */
export function furrowOf(walk: Walk, t: number): { at: Vec3; age: number }[] {
  const since = Math.min(FURROW_MS, t - walk.at);
  return Array.from({ length: FURROW_POINTS }, (_, i) => i / (FURROW_POINTS - 1))
    .filter((age) => age * FURROW_MS <= since)
    .map((age) => ({ at: walkAt(walk, t - age * FURROW_MS), age }));
}
